"use client"

import { useState, useEffect } from "react"
import type { AxiosResponse } from "axios"
import Backdrop from "components/Backdrop"
import { fetchEmployeeOwnProfileAPI } from "api/apiHelper"
import ErrorBox from "components/ErrorBox"
import Banner from "components/Banner"
import General from "components/General"
import IconButton from "components/IconButton"
import EmployeeDependentCard from "features/employees/EmployeeDependentsCard"
import { usePermissions } from "app/hooks"
import StatisticsCard from "components/StatisticsCard"
import HolidayCard from "components/HolidayCard"
import AnnouncementCard from "components/AnnouncementCard"
import LevelSystemCard from "features/organization_structure/LevelSystemCard"
import WelcomeGreeting from "components/WelcomeGreeting"
import { Link } from "react-router-dom"
import type { EmployeeLeaveInfoType, EmployeeLeavesDetailsType } from "types/leaves-types"
import {
  getEmployeeLeavesInfoByUser,
  getEmployeeLeavesDetailsByUser,
  getAllAnnouncements,
  getAllHolidays,
} from "api/apiHelper"
import OrganizationStructureCard from "features/organization_structure/OrganizationStructureCard"
import ManagerCalendarView from "features/leaves/ManagerCalendarView"

const EmployeesProfile = () => {
  const [employee, setEmployee] = useState<EmployeeType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>("")
  const permissions = usePermissions() || []
  const [leaveInfo, setLeaveInfo] = useState<EmployeeLeaveInfoType[] | []>([])
  const [leavesDetail, setLeavesDetail] = useState<EmployeeLeavesDetailsType[] | []>([])
  const [holidays, setHolidays] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const leaveTypeSettings: any = {
    "Annual Leave": { color: "blue", icon: "calendar" },
    "Sick Leave": { color: "red", icon: "medkit" },
    "Personal Leave": { color: "green", icon: "user" },
  }

  const consolidateLeaveData = (data: EmployeeLeaveInfoType[]): EmployeeLeaveInfoType[] => {
    if (!data || data.length === 0) return []

    const consolidated: { [key: string]: EmployeeLeaveInfoType } = {}

    data.forEach((leaveInfo) => {
      const leaveType = leaveInfo.leave_type_description

      if (consolidated[leaveType]) {
        consolidated[leaveType] = {
          ...consolidated[leaveType],
          max_leaves_allowed: (consolidated[leaveType].max_leaves_allowed || 0) + (leaveInfo.max_leaves_allowed || 0),
          remaining_leaves: (consolidated[leaveType].remaining_leaves || 0) + (leaveInfo.remaining_leaves || 0),
        }
      } else {
        consolidated[leaveType] = { ...leaveInfo }
      }
    })

    return Object.values(consolidated)
  }

  const consolidatedLeaveInfo = consolidateLeaveData(leaveInfo)

  useEffect(() => {
    const getEmployeeDetails = async () => {
      try {
        const response: AxiosResponse<EmployeeType> = await fetchEmployeeOwnProfileAPI()
        console.log("Profile Details : ", response.data)
        setEmployee(response.data)
        setIsLoading(false)
      } catch (error: any) {
        console.log(error)
        setError(error.message)
        setIsLoading(false)
      }
    }

    getEmployeeDetails()
  }, [])

  useEffect(() => {
    const fetchBulletinData = async () => {
      try {
        const promises: Promise<any>[] = []

        if (permissions?.includes("bulletin.view_holiday")) {
          promises.push(getAllHolidays())
        } else {
          promises.push(Promise.resolve({ data: [] }))
        }

        if (permissions?.includes("bulletin.view_announcement")) {
          promises.push(getAllAnnouncements())
        } else {
          promises.push(Promise.resolve({ data: [] }))
        }

        const [holidayRes, announcementRes] = await Promise.all(promises)

        if (permissions.includes("bulletin.view_holiday")) {
          setHolidays(holidayRes.data)
        }

        if (permissions.includes("bulletin.view_announcement")) {
          setAnnouncements(announcementRes.data)
        }
      } catch (error: any) {
        console.error(error)
        setError(error.message)
      }
    }

    fetchBulletinData()
  }, [])

  const fetchLeaves = async () => {
    if (permissions && permissions.includes("leaves.view_my_employeeleaveinfo")) {
      try {
        const response = await getEmployeeLeavesInfoByUser()
        const filteredLeaves = response.data.filter((leave: EmployeeLeaveInfoType) => {
          const leavePeriodStart = new Date(leave.period_start)
          const currentYear = new Date().getFullYear()
          return leavePeriodStart.getFullYear() === currentYear
        })
        setLeaveInfo(filteredLeaves)
      } catch (error: any) {
        console.log(error)
        setError(error.message)
      }
    }

    if (permissions && permissions.includes("leaves.view_my_employeeleavesdetail")) {
      try {
        const response = await getEmployeeLeavesDetailsByUser()
        setLeavesDetail(response.data)
      } catch (error: any) {
        console.log(error)
        setError(error.message)
      }
    }
  }

  useEffect(() => {
    fetchLeaves()
  }, [])

  return (
    <div className="mt-5">
      {isLoading ? (
        <Backdrop show={isLoading} />
      ) : (
        <div>
          {error && <ErrorBox errorMessage={error} />}

          {employee && (
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <WelcomeGreeting name={employee.first_name} />
                <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                  {permissions && permissions.includes("employees.change_my_employee") && (
                    <IconButton
                      url="/edit-profile"
                      name="Edit Profile"
                      icon="fas fa-edit"
                      extraStyles="bg-blue-600 hover:bg-blue-700 text-white"
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
                    <Banner employee={employee} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {consolidatedLeaveInfo?.map((leaveInfo: EmployeeLeaveInfoType, index: number) => {
                      return (
                        <StatisticsCard
                          key={index}
                          title={leaveInfo.leave_type_description}
                          used={leaveInfo.max_leaves_allowed - leaveInfo.remaining_leaves || 0}
                          total={leaveInfo.max_leaves_allowed || 0}
                          icon={leaveTypeSettings[leaveInfo.leave_type_description]?.icon || "user"}
                          color={leaveTypeSettings[leaveInfo.leave_type_description]?.color || "blue"}
                        />
                      )
                    })}
                  </div>

                  <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Employee Information</h2>
                      <General employee={employee} />
                    </div>
                  </div>

                  {permissions && permissions.includes("employees.view_my_employeedependants") && (
                    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
                      <EmployeeDependentCard dependents={employee.dependents} />
                    </div>
                  )}

                  <OrganizationStructureCard
                    currentEmployee={{
                      id: employee.id?.toString() || "",
                    }}
                    reportingChain={[]}
                    permissions={permissions}
                  />
                </div>

                <div className="space-y-6">
                  <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Quick Actions</h2>
                      <div className="grid grid-cols-2 gap-3">
                        <Link
                          to="/leaves/"
                          className="flex flex-col items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <i className="fas fa-calendar-plus text-blue-600 dark:text-blue-400 text-2xl mb-2"></i>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Request Leave</span>
                        </Link>
                        {permissions && permissions.includes("contracts.view_my_contract") && employee.contract_id && (
                          <Link
                            to="/my-contract"
                            className="flex flex-col items-center justify-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                          >
                            <i className="fas fa-clock text-green-600 dark:text-green-400 text-2xl mb-2"></i>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">View Contract</span>
                          </Link>
                        )}
                        <Link
                          to="/my-payslip"
                          className="flex flex-col items-center justify-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                        >
                          <i className="fas fa-file-invoice text-purple-600 dark:text-purple-400 text-2xl mb-2"></i>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">View Payslips</span>
                        </Link>
                        <Link
                          to="/policy"
                          className="flex flex-col items-center justify-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                        >
                          <i className="fas fa-question-circle text-amber-600 dark:text-amber-400 text-2xl mb-2"></i>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Policies</span>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Team Leave Calendar - Shows only for managers */}
                  <ManagerCalendarView />

                  {permissions && permissions.includes("bulletin.view_holiday") && (
                    <HolidayCard holidays={holidays} permissions={permissions} />
                  )}

                  {permissions && permissions.includes("bulletin.view_announcement") && (
                    <AnnouncementCard announcements={announcements} permissions={permissions} />
                  )}

                  <LevelSystemCard employeeData={employee} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EmployeesProfile